import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import DsLogo from './DsLogo';
import DropdownProfile from '../../../components/header/dropdown/profile';

// Klargo Design System v4.7 — the authenticated shell (Klargo-Reference's
// AuthShell.tsx), ported for the destinations KlargoReact actually has today.
// Fordon and Affärshistorik render as visible-but-disabled "coming soon"
// entries rather than dead links, since those pages don't exist yet — real
// links replace them once those routes are built. Desktop uses the reference's
// own ds-auth-rail so the wordmark starts on the same left edge as the page
// title below it; the account/language control stays the DropdownProfile
// grammar already established elsewhere in this app rather than the
// reference's own separate pill/panel markup.
const OVERVIEW_PATH = '/user/ChooseAction';

export default function AuthShell({ t, activeKey, children }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButtonRef = useRef(null);

  const closeMore = useCallback(() => {
    setMoreOpen((open) => {
      if (open) window.setTimeout(() => moreButtonRef.current?.focus(), 0);
      return false;
    });
  }, []);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMore();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [moreOpen, closeMore]);

  const upcoming = [];

  const destinations = [
    { key: 'overview', label: t('nav.overview'), path: OVERVIEW_PATH, enabled: true },
    { key: 'vehicles', label: t('nav.vehicles'), path: '/fordon', enabled: true },
    { key: 'history', label: t('nav.history'), path: '/affarshistorik', enabled: true },
  ];
  const tabs = [
    { key: 'overview', label: t('nav.overview'), path: OVERVIEW_PATH, enabled: true },
    { key: 'vehicles', label: t('nav.vehicles'), path: '/fordon', enabled: true },
    { key: 'history', label: t('nav.historyShort'), path: '/affarshistorik', enabled: true },
    { key: 'profile', label: t('profile.menu.profile'), path: '/Profile', enabled: true },
  ];

  const navLink = (item) => {
    const isActive = activeKey === item.key;
    if (!item.enabled) {
      return (
        <span
          key={item.key}
          aria-disabled="true"
          className="flex h-[60px] cursor-not-allowed items-center gap-1.5 px-3.5 text-[15px] font-medium text-[rgba(243,246,247,.4)]"
        >
          <span className="inline-block py-[3px]">{item.label}</span>
          <span
            className="rounded-full px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-wide"
            style={{ background: 'rgba(247,249,249,.12)' }}
          >
            {t('nav.comingSoon')}
          </span>
        </span>
      );
    }
    return (
      <Link
        key={item.key}
        to={item.path}
        aria-current={isActive ? 'page' : undefined}
        className={`flex h-[60px] items-center px-3.5 text-[15px] no-underline transition-colors hover:text-white active:translate-y-px ${
          isActive ? 'font-semibold text-white' : 'font-medium text-[rgba(243,246,247,.82)]'
        }`}
      >
        <span
          className="inline-block py-[3px] transition-colors"
          style={{ borderBottom: `2px solid ${isActive ? 'var(--ds-green-nav)' : 'transparent'}` }}
        >
          {item.label}
        </span>
      </Link>
    );
  };

  const tabLink = (tab) => {
    const isActive = activeKey === tab.key;
    if (!tab.enabled) {
      return (
        <span
          key={tab.key}
          aria-disabled="true"
          className="flex min-h-[56px] flex-1 cursor-not-allowed flex-col items-center justify-center px-1 pb-2 pt-1.5 text-ds-muted opacity-50"
        >
          <span aria-hidden="true" className="h-[3px] w-[22px] rounded-sm" />
          <span className="mt-[5px] text-[12.5px] font-semibold">{tab.label}</span>
        </span>
      );
    }
    return (
      <Link
        key={tab.key}
        to={tab.path}
        aria-current={isActive ? 'page' : undefined}
        className={`flex min-h-[56px] flex-1 flex-col items-center justify-center px-1 pb-2 pt-1.5 no-underline active:scale-[.96] ${
          isActive ? 'text-ds-green' : 'text-ds-muted'
        }`}
      >
        <span
          aria-hidden="true"
          className="h-[3px] w-[22px] rounded-sm transition-colors"
          style={{ background: isActive ? 'var(--ds-green)' : 'transparent' }}
        />
        <span className="mt-[5px] text-[12.5px] font-semibold">{tab.label}</span>
      </Link>
    );
  };

  return (
    <div className="ds-scope flex min-h-screen min-w-[320px] flex-col bg-ds-canvas font-ds-sans text-ds-body">
      <a
        href="#main-content"
        className="absolute left-[-9999px] top-0 z-[90] text-sm font-semibold leading-tight no-underline focus:left-3 focus:top-3 focus:rounded-[9px] focus:bg-ds-sheet focus:px-[18px] focus:py-3.5 focus:text-ds-ink"
      >
        {t('a11y.skipToContent')}
      </a>

      <header className="sticky top-0 z-50 hidden bg-ds-ink text-ds-ink-on-soft md:block" style={{ boxShadow: '0 1px 0 rgba(0,0,0,.25)' }}>
        <div className="ds-auth-rail flex h-[60px] items-center gap-[clamp(22px,3vw,40px)]">
          <Link to={OVERVIEW_PATH} aria-label="Klargo" className="flex items-center gap-2 px-0.5 py-3 no-underline">
            <DsLogo />
          </Link>
          <nav aria-label={t('nav.primary')} className="flex h-[60px] gap-0.5">
            {destinations.map(navLink)}
          </nav>
          <div className="ml-auto flex items-center">
            <DropdownProfile focused />
          </div>
        </div>
      </header>

      <header className="sticky top-0 z-50 bg-ds-ink text-ds-ink-on-soft md:hidden">
        <div className="flex h-[52px] items-center justify-between px-4">
          <Link to={OVERVIEW_PATH} aria-label="Klargo" className="flex items-center gap-[7px] px-0.5 py-3 no-underline">
            <DsLogo variant="mobile" />
          </Link>
          <DropdownProfile focused />
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="relative w-full flex-[1_1_auto] pb-[132px] outline-none md:pb-16">
        {children}
      </main>

      <nav
        aria-label={t('nav.primary')}
        className="fixed inset-x-0 bottom-0 z-50 flex border-t border-ds-rule-edge bg-ds-sheet md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {tabs.map(tabLink)}
      </nav>

      {/* Klargo-Reference renders an ink pill in this same spot that opens a
          dev-only demo-scenario switcher — its own comments call it "a
          deliberately non-product artefact" with no backend behind it, used
          to preview the authenticated product in different data states.
          There's nothing to switch here, so the same trigger/menu grammar
          (colour, radius, shadow, open/close animation) instead opens a
          short, honest list of what's still being built — the same "coming
          soon" destinations named in the nav above. Hidden entirely once
          nothing is left in `upcoming`, rather than showing an empty menu. */}
      {upcoming.length > 0 && (
      <div className="fixed bottom-[calc(66px+env(safe-area-inset-bottom))] right-3 z-[70] flex flex-col items-end md:bottom-4 md:right-4">
        {moreOpen && (
          <>
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              onClick={closeMore}
              className="fixed inset-0 z-[69] cursor-default border-none bg-transparent"
            />
            <div
              role="menu"
              aria-label={t('nav.comingSoon')}
              className="ds-animate-layer mb-2 min-w-[230px] rounded-[11px] bg-ds-ink p-1.5"
              style={{ boxShadow: '0 12px 30px rgba(10,20,26,.3)' }}
            >
              <div
                className="ds-mono px-2.5 pb-[5px] pt-[7px] text-[10.5px] tracking-[0.05em]"
                style={{ color: 'rgba(233,237,238,.55)' }}
              >
                {t('nav.upcomingHeading')}
              </div>
              {upcoming.map((item) => (
                <div
                  key={item.key}
                  role="menuitem"
                  aria-disabled="true"
                  className="flex w-full items-center justify-between gap-3 rounded-[7px] px-2.5 py-[13px] text-left text-[13px] font-normal"
                  style={{ color: 'rgba(233,237,238,.85)' }}
                >
                  <span>{item.label}</span>
                  <span className="ds-mono text-[10.5px]" style={{ color: 'rgba(233,237,238,.5)' }}>
                    {t('nav.comingSoon')}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        <button
          type="button"
          ref={moreButtonRef}
          onClick={() => setMoreOpen((open) => !open)}
          aria-expanded={moreOpen}
          aria-haspopup="menu"
          className="ds-mono inline-flex items-center gap-2 rounded-full bg-ds-ink px-4 py-3.5 text-[12.5px] text-ds-ink-on-soft transition-colors hover:bg-ds-ink-hover active:scale-[.97]"
          style={{ boxShadow: '0 6px 18px rgba(10,20,26,.28)' }}
        >
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--ds-green-dot)' }} />
          <span>{t('nav.comingSoon')}</span>
        </button>
      </div>
      )}
    </div>
  );
}
